import { useCallback, useEffect, useRef } from "react";
import { BackendSource, getFirehoseNewsFeed, isSourceAccessible } from ".";
import { NewsFeedItem, SOURCE_TIME_FIELDS } from "./models";
import { FETCH_STATUS } from "..";
import useFirehoseStore from "@/stores/FirehoseStore";
import { shallow } from "zustand/shallow";
import { playBeep, playSpoon } from "@/utils/sound-utils";
import useAuth from "@/hooks/useAuth";
import { useHasInternalNewsquawkUserRole } from "@/hooks/useHasInternalNewsquawkUserRole";
import { useGetAppRoles } from "@/hooks/useGetAppRoles";
import { useUserPreferences } from "@/pages/settings/hooks/useUserPreferences";
import { usePortfoliosWithSoundEnabled } from "./usePortfoliosWithSoundEnabled";
import firehoseSSEClient from "./firehoseSSEClient";
import { firehosePortfolioMatcher } from "@/widget-components/firehose-widgets/firehose-all/utils";

const STALENESS_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

/** Normalize source-specific published time into the generic source_published_time field */
function normalizeSourceTime(story: NewsFeedItem): void {
  const sourceTimeField = SOURCE_TIME_FIELDS[story.backend_source];
  if (sourceTimeField && (story as any)[sourceTimeField]) {
    story.source_published_time = (story as any)[sourceTimeField];
  }
}

/**
 * Custom React hook to fetch news feed data and subscribe to a Server-Sent Events (SSE) stream.
 * This hook is intended to be executed once on app load.
 * This hook itself does not return any data,
 * but rather manages the state of the news feed data
 * in the local store (FirehoseStore).
 *
 * @returns {Object} An object containing loading and error status flags.
 */
const useInitFirehoseStore = (): object => {
  const { accessToken, authenticated } = useAuth();

  const isInternalUser = useHasInternalNewsquawkUserRole();
  const roles = useGetAppRoles();

  // Refs so the SSE handler always reads the latest values without re-subscribing
  const accessTokenRef = useRef(accessToken);
  accessTokenRef.current = accessToken;
  const rolesRef = useRef(roles);
  rolesRef.current = roles;
  const isInternalUserRef = useRef(isInternalUser);
  isInternalUserRef.current = isInternalUser;

  // Accessing the local store to manage stories
  const firehoseStore = useFirehoseStore(
    (state) => ({
      addStory: state.addStory,
      addStories: state.addAllStories,
      addTypeStory: state.addTypeStory,
      clearAllStories: state.clearAllStories,
      status: state.status,
      setStatus: state.setStatus,
    }),
    shallow
  );

  // Derived state to indicate if data is fetched successfully
  const isFetched = firehoseStore.status == FETCH_STATUS.SUCCESS;

  // Derived state to indicate if data is currently being loaded
  const isLoading = firehoseStore.status == FETCH_STATUS.LOADING;

  // Derived state to indicate if there was an error during data fetching
  const isError = firehoseStore.status == FETCH_STATUS.ERROR;

  const { notificationsSound, isLoading: isUserPrefLoading } =
    useUserPreferences();

  // Keep a stable reference to portfolios with sound enabled (based on alert mode setting)
  const portfoliosWithSoundEnabledRef = usePortfoliosWithSoundEnabled();

  /**
   * Fetches the news feed data from the REST endpoint.
   * Updates the local store with the fetched stories.
   */
  const fetchNewsFeed = useCallback(async () => {
    firehoseStore.setStatus(FETCH_STATUS.LOADING);

    try {
      const response = (await getFirehoseNewsFeed()) as NewsFeedItem[];
      firehoseStore.setStatus(FETCH_STATUS.SUCCESS);

      // sort stories by id in descending order
      const responseSorted = response.sort((a, b) => b.id - a.id);

      const filteredResponse = responseSorted.filter((story) =>
        isSourceAccessible(story.backend_source, roles, isInternalUser)
      );
      filteredResponse.forEach(normalizeSourceTime);
      firehoseStore.addStories(filteredResponse);
    } catch (error) {
      firehoseStore.setStatus(FETCH_STATUS.ERROR);
      console.log(error);
    }
  }, [firehoseStore, isInternalUser, roles]);

  // useEffect to fetch news feed data
  useEffect(() => {
    if (!authenticated) return;

    if (!isFetched && !isLoading) fetchNewsFeed();
  }, [authenticated, isFetched, isLoading, fetchNewsFeed]);

  // Keep stored SSE params up to date when the token refreshes,
  // so that error-triggered reconnects use the latest token — without
  // tearing down the existing connection.
  useEffect(() => {
    if (accessToken) {
      firehoseSSEClient.updateUrlParams({ token: accessToken });
    }
  }, [accessToken]);

  useEffect(() => {
    if (!authenticated || !accessTokenRef.current) return;

    if (isUserPrefLoading) return;

    firehoseSSEClient.start({
      urlParams: {
        token: accessTokenRef.current,
      },
    });

    const unsubStatusChange = firehoseSSEClient.subscribe(
      "onStatusChange",
      (newStatus) => {
        if (newStatus === "active") {
          // fetchNewsFeed();
        }
      }
    );

    const unsubMessage = firehoseSSEClient.subscribe(
      "message",
      (newStory: NewsFeedItem) => {
        newStory.isNew = true;
        newStory.received_at = Date.now();
        normalizeSourceTime(newStory);

        // Filter out inaccessible sources based on user roles
        if (!isSourceAccessible(newStory.backend_source, rolesRef.current, isInternalUserRef.current)) {
          return;
        }

        // Add the new story to the local store
        // This will add the story to the `allStories` and the appropriate field based on its backend source.
        firehoseStore.addStory(newStory);

        /**
         * stocks-news is a special type of story.
         * These are stories with a source of stocks-one, stocks-two, stocks-three.
         */
        const stocksSources: BackendSource[] = [
          "stocks-one",
          "stocks-two",
          "stocks-three",
          "stocks-four",
          "stocks-four-filings",
        ];

        let stocksNewsStory = false;

        if (stocksSources.includes(newStory.backend_source)) {
          firehoseStore.addTypeStory(newStory, ["stocks-news"]);
          stocksNewsStory = true;
        }

        // -----------
        // Play a sound if the story is from a highlighted portfolio
        // or it is a truth social story.
        // Skip sounds for stale stories to prevent flooding on backend restart/backfill.
        // Fail-open: only gates when source_published_time exists.
        // -----------
        const isStale =
          newStory.source_published_time != null &&
          Date.now() - new Date(newStory.source_published_time).getTime() >
            STALENESS_THRESHOLD_MS;

        if (!isStale) {
          const portfoliosForSource =
            portfoliosWithSoundEnabledRef.current?.[
              newStory.backend_source
            ] ?? [];
          const portfoliosForAll =
            portfoliosWithSoundEnabledRef.current?.allStories ?? [];
          let portfoliosToMatch = [...portfoliosForSource, ...portfoliosForAll];

          if (stocksNewsStory) {
            const portfoliosForStocksNews =
              portfoliosWithSoundEnabledRef.current?.["stocks-news"] ??
              [];
            portfoliosToMatch = [
              ...portfoliosToMatch,
              ...portfoliosForStocksNews,
            ];
          }

          const matchedPortfolio = firehosePortfolioMatcher(
            newStory,
            portfoliosToMatch
          );

          if (matchedPortfolio) {
            playSpoon();
          } else if (
            // Play a sound if the story is from Truth Social
            // and the user has enabled notifications for Truth Social
            newStory.backend_source === "truth-social" &&
            notificationsSound.truth_social
          ) {
            playBeep();
          }
        }
      }
    );

    // terminating the connection on component unmount
    // Cleanup function to close the SSE connection on component unmount
    return () => {
      unsubStatusChange();
      unsubMessage();
    };
  }, [authenticated, isUserPrefLoading, notificationsSound.truth_social]);

  return {
    isLoading,
    isError,
  };
};

export default useInitFirehoseStore;
